import { Filter } from "@/shared/lib/filter/filter";
import { Representative } from "@/services/backend/representatives/types/representative";
import { Address } from "@/services/backend/addresses/types/address";
import {
  MonitorIoTClass,
  MonitorType,
} from "@/services/backend/qpm/types/enums";
import {
  DocumentBase,
  DocumentImmutability,
} from "@/services/backend/qpm/types/document";
import { ProcessArtefact } from "@/services/backend/qpm/types/process-artefact";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import { MonitorSnapDTO } from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import type { QpmCeoGender } from "@/services/backend/qpm/offer-config/types/offer-config";
import {
  ChangeMode,
  ContractStartMode,
  ContractTermMode,
  ConfigOrigin,
  ExtensionAreaScope,
  IoTAdditionalTrapRentMode,
  IoTTableMode,
  MainOfferKind,
  OfferType,
  PriorIoTPresenceState,
  SectionMode,
  TemplateScenario,
} from "@/services/backend/qpm/offers/types/offer-enum";

export interface CreateRequest {
  id: string;
  customerId: string;
  processId: string;
  catalogId?: string;
  companyId: string;
  branchId: string;
  documentProfile?: CreateOfferDocumentProfile;
}

export interface ConfigValue<T> {
  value: T;
  origin: ConfigOrigin;
}

export interface OfferExtensionDocumentProfile {
  priorIoTPresenceState: ConfigValue<PriorIoTPresenceState>;
  tableMode: ConfigValue<SectionMode>;
  ioTInTableMode: ConfigValue<IoTTableMode>;
  ioTRentalContractMode: ConfigValue<SectionMode>;
  referencedContractDate?: string | null;
  contractAgainstText: ConfigValue<string>;
  ioTRentalServiceText?: ConfigValue<string>;
  targetAreaScope: ConfigValue<ExtensionAreaScope>;
}

export interface OfferDocumentProfile {
  offerType: ConfigValue<OfferType>;
  mainOfferKind?: ConfigValue<MainOfferKind> | null;
  extension?: OfferExtensionDocumentProfile | null;
  templateScenario: TemplateScenario;
  templateSetVersion: string;
}

export interface CreateOfferDocumentProfile {
  offerType: ConfigValue<OfferType>;
  mainOfferKind?: ConfigValue<MainOfferKind>;
  priorIoTPresenceState?: ConfigValue<PriorIoTPresenceState>;
  tableMode?: ConfigValue<SectionMode>;
  ioTInTableMode?: ConfigValue<IoTTableMode>;
  ioTRentalContractMode?: ConfigValue<SectionMode>;
  referencedContractDate?: string | null;
  contractAgainstText?: ConfigValue<string>;
  ioTRentalServiceText?: ConfigValue<string>;
  targetAreaScope?: ConfigValue<ExtensionAreaScope>;
  templateSetVersion?: string;
}

export interface ConfigPatch<T> {
  mode: ChangeMode;
  value?: T;
}

export interface TargetOrganismSelection {
  categoryKeys: string[];
  typeKeys: string[];
  customText: string;
  origin: ConfigOrigin;
}

export interface TargetOrganismSelectionPatch {
  mode: ChangeMode;
  categoryKeys?: string[];
  typeKeys?: string[];
  customText?: string;
}

export interface ChangeOfferDocumentProfilePatch {
  offerType?: ConfigPatch<OfferType>;
  mainOfferKind?: ConfigPatch<MainOfferKind>;
  priorIoTPresenceState?: ConfigPatch<PriorIoTPresenceState>;
  tableMode?: ConfigPatch<SectionMode>;
  ioTInTableMode?: ConfigPatch<IoTTableMode>;
  ioTRentalContractMode?: ConfigPatch<SectionMode>;
  contractAgainstText?: ConfigPatch<string>;
  ioTRentalServiceText?: ConfigPatch<string>;
  targetAreaScope?: ConfigPatch<ExtensionAreaScope>;
  referencedContractDate?: string | null;
}

export interface ChangeOfferDocumentProfileRequest {
  offerId: string;
  documentProfile: ChangeOfferDocumentProfilePatch;
  targetOrganismSelection?: TargetOrganismSelectionPatch;
  ioTTargetOrganismSelection?: TargetOrganismSelectionPatch;
}

export interface Offer
  extends
    Omit<DocumentBase, "kind">,
    DocumentImmutability,
    OfferCalculationResult {
  id: string;

  /** document kind (should be "offer" for QPM offers). */
  kind: GSMDocumentKind;

  companyId?: string;
  branchId?: string;
  customerId: string;
  serviceObjectId?: string;

  createdAt: string;
  updatedAt: string;

  processArtefact?: ProcessArtefact;
  processId?: string;

  date: string;
  validUntilDate: string | null;
  certifiedTrainingCoursePrice: number;
  contractStartMode: ContractStartMode;
  contractStartDate: string | null;
  contractTermMode: ContractTermMode;
  contractTermText: string;
  contractTermUnlimitedText: string;
  contractTermTextSeparate: boolean;
  contractTermContractText: string;
  contractAdditionalText: string;
  contractSpecialAgreementUseDate: boolean;
  contractSpecialAgreementText: string;
  ioTAdditionalTrapRentEnabled: boolean;
  ioTAdditionalTrapRentMode: IoTAdditionalTrapRentMode;
  ioTAdditionalTrapRentPrice: number;
  ioTAdditionalTrapRentMonitorIds: string[];
  includeIoTCheckpointServiceInQpm: boolean;
  priceVisibility: OfferPriceVisibility;

  linkedCalculationId?: string;

  monitorSnaps: Record<string, MonitorSnapDTO>;

  discount: OfferDiscount;
  materialCost: OfferMaterialCost;
  flyExterminatorMaintenance: FlyExterminatorMaintenance;
  hirePurchaseFiv: HirePurchase;
  coverLetterText: OfferCoverLetterTextBlocks;
  generalTextBlocks: GeneralTextBlocks;
  installationDocumentation: ServiceDocumentation[];
  maintenanceDocumentation: ServiceDocumentation[];

  postalAddress: Address;
  postalAddressContactRole: string;
  serviceAddress: Address;
  representative: Representative;
  signatureId?: string;
  signatureLocation: string;
  signatureDate?: string | null;

  offerConfigSnapshot: OfferConfigSnapshot;
  certificateSnapshot: OfferCertificateSnapshot;
  companyReferenceSnapshot: OfferCompanyReferenceSnapshot;
  documentProfile: OfferDocumentProfile;
  targetOrganismSelection: TargetOrganismSelection;
  ioTTargetOrganismSelection: TargetOrganismSelection;
}

export interface PutCoverLetterTextBlocksRequest extends OfferCoverLetterTextBlocks {
  offerId: string;
}

export interface OfferCalculationResult {
  installationResult: OfferInstallationResult;
  maintenanceResult: OfferMaintenanceResult;
  rentingResult: OfferRentingResult;
}

export interface OfferInstallationResult {
  qpm: OfferResultEntry;
  fiv: OfferResultEntry;
  aLoRa: OfferResultEntry;
}

export interface OfferMaintenanceResult {
  qpm: OfferResultEntry;
  qpmCohorts: OfferResultEntry[];
  qpmIoTAddon: OfferResultEntry;
  scb: OfferResultEntry;
  fiv: OfferResultEntry;
  aLoRa: OfferResultEntry;
}

export interface OfferResultEntry {
  /** unit.Currency */
  totalCost: number;
  /** unit.Currency */
  costPerMeasure: number;
  amountOfMeasures: number;
}

export interface OfferRentingResult {
  rentingDuration: number;
  /** unit.Currency */
  rentPerMonth: number;
  /** unit.Currency */
  rentPerMonthWithMaintenance: number;
}

export interface OfferMaterialCost {
  qpm: OfferMaterialPriceList;
  fiv: OfferMaterialPriceList;
  aLoRa: OfferMaterialPriceList;
  additions: OfferMaterialAdditionPriceList;
}

export interface OfferPriceVisibility {
  material: OfferPriceVisibilityMaterial;
  installation: OfferPriceVisibilityInstallation;
  maintenance: OfferPriceVisibilityMaintenance;
  renting: OfferPriceVisibilityRenting;
}

export interface OfferPriceVisibilityMaterial {
  qpm: boolean;
  fiv: boolean;
  aLoRa: boolean;
  additions: boolean;
}

export interface OfferPriceVisibilityInstallation {
  qpm: boolean;
  fiv: boolean;
  aLoRa: boolean;
}

export interface OfferPriceVisibilityMaintenance {
  qpm: boolean;
  fiv: boolean;
  scb: boolean;
  aLoRa: boolean;
}

export interface OfferPriceVisibilityRenting {
  aLoRa: boolean;
}

export interface OfferMaterialPriceList {
  totalAmount: number;
  /** unit.Currency */
  totalCost: number;
  positions: OfferMaterialPricePosition[];
}

export interface OfferMaterialPricePosition {
  monitorName: string;
  amount: number;
  /** unit.Currency */
  price: number;
  /** unit.Currency */
  totalCost: number;
}

export interface OfferMaterialAdditionPriceList {
  totalAmount: number;
  totalCost: number;
  positions: OfferMaterialAdditionPricePosition[];
}

export interface OfferMaterialAdditionPricePosition {
  parentMonitorName: string;
  additionalId: string;
  name: string;
  materialMode: "BOUND_TO_MONITOR_MATERIAL" | "SEPARATE_MATERIAL";
  amount: number;
  price: number;
  totalCost: number;
}

export interface HirePurchase {
  isActive: boolean;
  isOptional: boolean;
  /** backend sends string */
  monitorType: MonitorType | string;
  monitorAmount: number;
  /** unit.Currency */
  totalCost: number;
  surcharge: number;
  /** unit.Currency */
  totalCostSurcharged: number;
  durationMonths: number;
  /** unit.Currency */
  ratePerMonth: number;
}

export interface OfferDiscount {
  isApplied: boolean;
  totalDiscountPercentage: number;
  discountTiers: DiscountTierList;
}

export interface DiscountTierList {
  tiers: DiscountTier[];
}

export interface DiscountTier {
  minimumMonitorAmount: number;
  /** unit.Percent (serialized as number) */
  percentage: number;
  isApplied: boolean;
}

export type PutRequest = Offer;

/** Normalized offer list returned by the QPM API adapter. */
export interface OfferList {
  items: Offer[];
  count: number;
}

export interface ListRequest {
  process: Filter<string>;
  createdAt: Filter<string>;
  updatedAt: Filter<string>;
  company: Filter<string>;
  branch: Filter<string>;
  customer: Filter<string>;
  serviceObject: Filter<string>;
  orderBy: { field: string; dsc: boolean }[];
  limit: number;
}

export interface ShowOfferRequest {
  id: string;
}

export interface ApplyCalculationDataRequest {
  customerId: string;
  processId?: string;
  offerId?: string;
  calculationId: string;
}

export interface CreateAndApplyCalculationDataRequest {
  offerId: string;
  customerId: string;
  processId: string;
  calculationId: string;
  companyId: string;
  branchId: string;
}

export interface SetOfferDateRequest {
  offerId: string;
  date: string;
}

export interface SetOfferImmutableRequest {
  id: string;
  reason: string;
  setBy: string;
}

export enum OfferImmutabilityReason {
  SetByUser = "set_by_user",
}

export interface SetOfferValidUntilDateRequest {
  offerId: string;
  validUntilDate: string | null;
}

export interface SetOfferCertifiedTrainingCoursePriceRequest {
  offerId: string;
  certifiedTrainingCoursePrice: number;
}

export interface SetOfferContractTermsRequest {
  offerId: string;
  contractStartMode: ContractStartMode;
  contractStartDate: string | null;
  contractTermMode: ContractTermMode;
  contractTermText: string;
  contractTermUnlimitedText: string;
  contractTermTextSeparate: boolean;
  contractTermContractText: string;
  contractAdditionalText: string;
  contractSpecialAgreementUseDate: boolean;
  contractSpecialAgreementText: string;
  ioTAdditionalTrapRentEnabled: boolean;
  ioTAdditionalTrapRentMode: IoTAdditionalTrapRentMode;
  ioTAdditionalTrapRentPrice: number;
  ioTAdditionalTrapRentMonitorIds: string[];
  includeIoTCheckpointServiceInQpm: boolean;
  priceVisibility: OfferPriceVisibility;
}

export interface UpdatePostalAddressRequest {
  id: string;
  postalAddress: Address;
  postalAddressContactRole?: string;
}

export interface UpdateServiceAddressRequest {
  id: string;
  serviceAddress: Address;
}

export interface UpdateRepresentativeRequest {
  id: string;
  representative: Representative;
}

export interface UpdateHirePurchaseRequest {
  offerId: string;
  isActive: boolean;
  isOptional: boolean;
  monitorType: MonitorType | string;
  surcharge: number;
  durationMonths: number;
}

export interface DeleteRequest {
  id: string;
}

export enum OfferDocument {
  ALL = "all",
  OFFER = "offer",
  MonitorCatalog = "monitorCatalog",
  QualityAssuranceRef = "qualityAssuranceRef",
  ServiceProgram = "serviceProgram",
  Contract = "contract",
  ORDER = "order",
}

export interface PreviewPDFRequest {
  offerId: string;
  document: OfferDocument;
  documents?: OfferDocument[];
}

export interface PDFRequest {
  offerId: string;
  document: OfferDocument;
  documents?: OfferDocument[];
  download: boolean;
  downloadBy: string;
}

/** Backend uses template.HTML (serialized as string). */
export interface OfferCoverLetterTextBlocks {
  coverLetterSalutation: string;
  coverLetterCustomText: string;
  coverLetterConceptText: string;
  coverLetterCustomerRequirements: string;
}

export interface GeneralTextBlocks {
  currentState: string;
  offerClaimText: string;
  displayOfferClaim: boolean;
}

export interface PutGeneralTextBlocksRequest {
  offerId: string;
  currentState: string;
  offerClaimText: string;
  displayOfferClaim: boolean;
}

export type OfferGeneralTextBlocksRes = "ok";

export interface SignRequest {
  id: string;
  signature: string;
  dataFormat: string;
  nameOfSigner: string;
  signatureLocation: string;
  signatureDate: string;
}

export interface ClearSignatureRequest {
  offerId: string;
}

export interface OfferSignature {
  nameOfSigner: string;
  data: string;
  dataFormat: string;
}

export interface OfferConfigSettingsSnapshot {
  ceoName: string;
  ceoGender?: QpmCeoGender;
  allowedSurcharges: number[];
  allowedDiscounts: number[];
  offerClaimText: string;
  serviceProgramGroups: ServiceProgramGroupSnapshot[];
  serviceProgramEntries: ServiceProgramEntrySnapshot[];
  serviceProgramPageBreaks?: ServiceProgramPageBreakSnapshot[] | null;
  serviceProgramPageBreaksInitialized?: boolean;
}

export interface ServiceProgramGroupSnapshot {
  id: string;
  section: string;
  order: number;
  title: string;
  subtitle: string;
}

export interface ServiceProgramEntrySnapshot {
  id: string;
  groupId: string;
  section: string;
  rowMode: string;
  pestCategoryMatchMode: ServiceProgramMatchMode;
  pestIndicatorMatchMode: ServiceProgramMatchMode;
  monitorTypeMatchMode: ServiceProgramMatchMode;
  ioTClassMatchMode: ServiceProgramMatchMode;
  order: number;
  body: string;
  pestCategoryKeys: string[];
  pestIndicatorKeys: string[];
  monitorTypes: MonitorType[];
  ioTClasses: MonitorIoTClass[];
}

export type ServiceProgramMatchMode = "any" | "all";

export interface ServiceProgramPageBreakSnapshot {
  id: string;
  anchorKind: "group" | "entry";
  anchorId: string;
  order: number;
}

export interface OfferConfigTextBlockSnapshot {
  id: string;
  section: string;
  order: number;
  title: string;
  body: string;
  alwaysDisplay: boolean;
}

export interface OfferConfigSnapshot {
  settings: OfferConfigSettingsSnapshot;
  textBlocks: OfferConfigTextBlockSnapshot[];
}

export interface PutOfferConfigSettingsSnapshotRequest {
  offerId: string;
  ceoName: string;
  ceoGender: QpmCeoGender;
  allowedSurcharges: number[];
  allowedDiscounts: number[];
  offerClaimText: string;
  serviceProgramGroups: ServiceProgramGroupSnapshot[];
  serviceProgramEntries: ServiceProgramEntrySnapshot[];
  serviceProgramPageBreaks: ServiceProgramPageBreakSnapshot[];
  serviceProgramPageBreaksInitialized: boolean;
}

export interface PatchOfferConfigTextBlockSnapshotBodyRequest {
  offerId: string;
  id: string;
  body: string;
}

export interface PatchOfferConfigTextBlockSnapshotTitleRequest {
  offerId: string;
  id: string;
  title: string;
}

export interface PatchOfferConfigTextBlockSnapshotAlwaysDisplayRequest {
  offerId: string;
  id: string;
  alwaysDisplay: boolean;
}

export interface ReorderOfferConfigTextBlocksSnapshotRequest {
  offerId: string;
  section: string;
  orderedIds: string[];
}

export interface CreateOfferConfigTextBlockSnapshotRequest {
  offerId: string;
  section: string;
  title: string;
  body: string;
  alwaysDisplay: boolean;
}

export interface DeleteOfferConfigTextBlockSnapshotRequest {
  offerId: string;
  id: string;
}

export interface ReloadOfferConfigSnapshotRequest {
  offerId: string;
}

export interface OfferCertificateItemSnapshot {
  id: string;
  name: string;
  alwaysDisplay: boolean;
  /** ISO string */
  takenAt: string;
}

export interface OfferCertificateSnapshot {
  certificates: OfferCertificateItemSnapshot[];
  /** ISO string */
  takenAt: string;
}

export interface AddCertificateToOfferRequest {
  offerId: string;
  certificateId: string;
}

export interface RemoveCertificateFromOfferRequest {
  offerId: string;
  certificateId: string;
}

export interface UpdateOfferCertificateRequest {
  offerId: string;
  certificateId: string;
  name: string;
  alwaysDisplay: boolean;
}

export interface AddCompanyReferenceToOfferRequest {
  offerId: string;
  companyReferenceId: string;
}

export interface AddCompanyReferencesToOfferRequest {
  offerId: string;
  companyReferenceIds: string[];
}

export interface UpdateOfferCompanyReferenceRequest {
  offerId: string;
  companyReferenceId: string;
  display: boolean;
  branch: string;
  companyName: string;
}

export interface DeleteOfferCompanyReferenceRequest {
  offerId: string;
  companyReferenceId: string;
}

export interface OfferCompanyReferenceSnapshot {
  references: OfferCompanyReferenceItemSnapshot[];
  /** ISO string */
  takenAt: string;
}

export interface OfferCompanyReferenceItemSnapshot {
  display: boolean;
  snapshot: CompanyReferenceSnapshot;
  /** ISO string */
  takenAt: string;
}

export interface CompanyReferenceSnapshot {
  sourceId: string;
  branch: string;
  companyName: string;
  /** ISO string */
  takenAt: string;
}

export interface OfferIsReadyForCustomerRequest {
  offerId: string;
}

export interface CreateFromOfferRequest {
  id: string;
  processId: string;
  sourceOfferId: string;
  companyId: string;
  branchId: string;
}

export interface UpdateFlyExterminatorMaintenanceDiscountRequest {
  offerId: string;
  discount: number;
}

export interface FlyExterminatorMaintenance {
  discount: number;
  cohorts: FlyExterminatorMaintenanceCohort[];
}

export interface FlyExterminatorMaintenanceCohort {
  monitorAmount: number;
  amountOfMeasures: number;
  pricePerMeasure: number;
  discountedPricePerMeasure: number;
  adhesiveCadence: string;
  tubeCadence: string;
}

export interface ServiceDocumentation {
  documentation: string;
  render: boolean;
}
